import {
  Box,
  Button,
  CircularProgress,
  Collapse,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from '@mui/material';

import { Fragment, useState } from 'react';
import CheckIcon from '@mui/icons-material/Check';
import ErrorIcon from '@mui/icons-material/Error';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { RefreshRounded } from '@mui/icons-material';
import { observer } from 'mobx-react';
import { timestampToDate } from '../utils/date';
import Loader from './loader';
import { useVouchers, useRetryVoucherMutation } from '../stores/FetcherStores/BookkeepingFetcher';
import useCompanyId from '../hooks/useCompanyId';

const PAGE_SIZE = 10;

const Bookkeeping = () => {
  const [openKey, setOpenKey] = useState<string | null>(null);
  const [page, setPage] = useState(0);

  const companyId = useCompanyId();
  const {
    data: vouchers,
    isLoadingInitial: isVouchersLoading,
    isLoadingMore: isVouchersLoadingMore,
    fetchNextPage: fetchNextVouchers,
    refetch: refetchVouchers,
  } = useVouchers(companyId);
  const { mutate: retryVoucher, isLoading: isRetryingVoucher } = useRetryVoucherMutation();

  if (isVouchersLoading) {
    return (
      <div className="loaderContainer">
        <div className="loadingInnerContainer">
          <Loader />
        </div>
      </div>
    );
  }
  if (vouchers.length === 0) {
    return <Typography>Detta företag har ingen bokföringshistorik</Typography>;
  }

  return (
    <Box>
      <TableContainer component={Paper} sx={{ mt: 2 }}>
        <Table size="medium">
          <TableHead>
            <TableRow>
              <TableCell>Beskrivning</TableCell>
              <TableCell>ID</TableCell>
              <TableCell align="right">Total</TableCell>
              <TableCell>Transaktionsdatum</TableCell>
              <TableCell align="right">Verifikationsnummer</TableCell>
              <TableCell>Verifikationserie</TableCell>
              <TableCell>Status</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {vouchers.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE).map((voucher) => (
              <Fragment key={voucher.id}>
                <TableRow sx={{ '& > td': { borderBottom: 'none' }, p: 0 }}>
                  <TableCell>{voucher.voucher.Description}</TableCell>
                  <TableCell>{voucher.id}</TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                    {voucher.voucher.total && `${voucher.voucher.total} kr`}
                  </TableCell>
                  <TableCell>{timestampToDate(voucher.date)?.toISOString()}</TableCell>
                  <TableCell align="right">{voucher.voucher.voucherNumber}</TableCell>
                  <TableCell>{voucher.voucher.VoucherSeries}</TableCell>
                  <TableCell>
                    {voucher.status === 'COMPLETE' ? (
                      <CheckIcon color="success"> </CheckIcon>
                    ) : (
                      <ErrorIcon color="error" />
                    )}
                  </TableCell>
                  <TableCell>
                    <IconButton
                      aria-label="expand row"
                      size="small"
                      onClick={() => {
                        if (openKey === voucher.id) {
                          setOpenKey(null);
                        } else {
                          setOpenKey(voucher.id);
                        }
                      }}
                    >
                      {voucher.id === openKey ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                    </IconButton>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={12}>
                    <Collapse in={voucher.id === openKey} timeout="auto" unmountOnExit>
                      <Box>
                        <Typography variant="h6">Verifikationsrader</Typography>
                        <Box sx={{ marginBottom: 1 }}>
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell>Konto</TableCell>
                                <TableCell align="right">Kredit</TableCell>
                                <TableCell align="right">Debit</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {Object.entries(voucher.voucher.VoucherRows).map(([key, item]) => (
                                <TableRow
                                  key={key}
                                  sx={{ '& > td': { borderBottom: 'none' }, p: 0 }}
                                >
                                  <TableCell>{item.Account}</TableCell>
                                  <TableCell align="right">{item.Credit}</TableCell>
                                  <TableCell align="right">{item.Debit}</TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </Box>
                      </Box>
                      {voucher.errorMessage && voucher.status === 'FAILED' && (
                        <Box
                          sx={{
                            width: '250px',
                            marginBottom: 1,
                          }}
                        >
                          <Typography variant="h6">Fel:</Typography>
                          <Typography variant="body2" gutterBottom component="div">
                            {voucher.errorMessage}
                          </Typography>
                          {!isRetryingVoucher ? (
                            <Button
                              variant="outlined"
                              color="error"
                              onClick={() => {
                                retryVoucher({ companyKey: companyId, voucherKey: voucher.id });
                              }}
                            >
                              Försök igen
                            </Button>
                          ) : (
                            <Box
                              sx={{
                                width: '30px',
                              }}
                            >
                              <Loader />
                            </Box>
                          )}
                        </Box>
                      )}
                    </Collapse>
                  </TableCell>
                </TableRow>
              </Fragment>
            ))}
          </TableBody>
        </Table>
        <Box sx={{ flexDirection: 'row', display: 'flex', justifyContent: 'space-between', p: 1 }}>
          <Box sx={{ flexDirection: 'row', display: 'flex' }}>
            <TablePagination
              count={vouchers.length}
              rowsPerPageOptions={[10]}
              component={Box}
              rowsPerPage={PAGE_SIZE}
              page={page}
              onPageChange={(e, p) => {
                setPage(p);
                if (p * PAGE_SIZE + 20 > vouchers.length) {
                  fetchNextVouchers();
                }
              }}
            />
            {isVouchersLoadingMore && <CircularProgress color="secondary" />}
          </Box>
          <IconButton
            aria-label="refresh"
            size="small"
            onClick={() => {
              refetchVouchers();
              setPage(0);
              setOpenKey(null);
            }}
          >
            <RefreshRounded />
          </IconButton>
        </Box>
      </TableContainer>
    </Box>
  );
};

export default observer(Bookkeeping);
